import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getDevice,
  getDevicesByIds,
  getDevicesBySearch,
  getPaginatedDevicesFromRpc,
} from "@/api/resources/devices/api";
import i18n from "@/providers/i18n";

import type { DeviceDetailCorrected, ListDevicesProps, SearchDevicesProps } from "./types";

const entity = i18n.t("device");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("devices");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const devicesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "devices"],
  lists: () => [...devicesQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...devicesQueryKeys.lists(), "byIds", ids],
  listPaginatedFromRpc: (queryKeyPaginationFilters: ListDevicesProps) => [
    ...devicesQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: SearchDevicesProps) => [
    ...devicesQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...devicesQueryKeys.all(), "byId", id],
};

export const devicesQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...devicesQueryKeys.listByIds(ids)],
      queryFn: () =>
        getDevicesByIds({
          keyIn: "ids",
          idsIn: ids,
        }) as Promise<DeviceDetailCorrected[]>,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchDevicesProps) =>
    queryOptions({
      queryKey: [...devicesQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getDevicesBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginatedFromRpc: (queryKeyPaginationFilters: ListDevicesProps) =>
    queryOptions({
      queryKey: [...devicesQueryKeys.listPaginatedFromRpc(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedDevicesFromRpc(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...devicesQueryKeys.byId(id)],
      queryFn: id ? () => getDevice({ _id: id }) as Promise<DeviceDetailCorrected | null> : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
